import {
  ScrollableList,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@ps/isp-web-component-library';
import type { MeetingBasedEventTypeId } from '@ps/isp-web-services-types';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { TranslationTag } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { ExpandablePanel, MarketTemplate, Title } from '@swf/sports/components';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { EventTypeId } from '@swf/sports/types/eventType';
import { isMeetingBasedEventTypeId } from '@swf/sports/utils/isEventTypeId';

import {
  TXTCLI_All,
  TXTWCL_Next_Greyhound_Races,
  TXTWCL_Next_Horse_Races,
  TXTWCL_UK_X_Ireland
} from './SportsNextRacesWidget.messages.json';
import styles from './SportsNextRacesWidget.scss';
import RaceInfo from './components/RaceInfo';
import ViewFullRaceCard from './components/ViewFullRaceCard';
import { sportsNextRacesWidgetModel, useSportsNextRacesWidget } from './model';
import type { Template } from './model/types';

const translationMap: Record<MeetingBasedEventTypeId, TranslationTag> = {
  [EventTypeId.HORSE_RACING]: TXTWCL_Next_Horse_Races,
  [EventTypeId.GREYHOUND_RACING]: TXTWCL_Next_Greyhound_Races
};

const SportsNextRacesWidget: WidgetFC = () => {
  const { tabClicked } = useTrackers();
  const { widgetDispatch, widgetSelect, widgetConfig } = useSportsNextRacesWidget();
  const templates = useSelector(widgetSelect.templates);
  const ukIeTemplates = useSelector(widgetSelect.ukIeTemplates);
  const races = useSelector(widgetSelect.races);
  const markets = useSelector(widgetSelect.markets);
  const errorCode = useSelector(widgetSelect.errorCode);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const isLoading = useSelector(widgetSelect.isLoading);
  const selectedTemplate = useSelector(widgetSelect.selectedTemplate);
  const marketIds = useSelector(widgetSelect.marketIds);
  const selectedTab = useSelector(widgetSelect.selectedTab);
  const site = useSelector(select.cms.getSiteId);

  const { eventTypeId: urlParamEventTypeId } = useSelector(select.sports.getUrlParams);

  const allRacesTrackingLabel = useTrackingLabel(TXTCLI_All);
  const ukIeRacesTrackingLabel = useTrackingLabel(TXTWCL_UK_X_Ireland);

  const isCondensedView = widgetConfig.condensed;
  const configuredEventTypeId = widgetConfig.eventTypeId;

  useEffect(() => {
    if (!selectedTab && templates.length) {
      const initialTab = site === Sites.UK && ukIeTemplates.length ? 'ukIe' : 'all';
      widgetDispatch.setSelectedTab(initialTab);
    }
  }, [selectedTab, widgetDispatch, site, ukIeTemplates.length, templates.length]);

  const getRaceWithInfoFromTemplate = useCallback(
    (template: Template) => {
      // TODO: avoid assertion of optional marketId
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { raceId } = markets[template.marketId]!;
      return {
        ...races[raceId as string]
      };
    },
    [races, markets]
  );

  const renderList = useCallback(
    (templatesList: Template[]) =>
      templatesList.map((template) => {
        const isActive = selectedTemplate?.marketId === template.marketId;
        if (!isActive && template.hasFinished) return null;
        const { startTime, venue } = getRaceWithInfoFromTemplate(template);

        return (
          <ScrollableList.Tab
            variant="stack"
            key={template.marketId}
            active={isActive}
            onClick={() =>
              widgetDispatch.setSelected({
                ...template,
                eventTypeId: Number(configuredEventTypeId ?? urlParamEventTypeId)
              })
            }
          >
            <FormattedDateTime date={startTime} />
            <span className={styles.venue}>{venue}</span>
          </ScrollableList.Tab>
        );
      }),
    [
      selectedTemplate,
      widgetDispatch,
      configuredEventTypeId,
      urlParamEventTypeId,
      getRaceWithInfoFromTemplate
    ]
  );

  const handleTabClick = (trackingLabel: string | undefined) => {
    tabClicked({
      tabName: trackingLabel ?? ''
    });
  };

  const handleTabChange = (value: string) => {
    widgetDispatch.setSelectedTab(value);
    const currentTabTemplates = value === 'ukIe' ? ukIeTemplates : templates;

    if (currentTabTemplates.length) {
      widgetDispatch.setSelected({
        ...currentTabTemplates[0],
        eventTypeId: Number(configuredEventTypeId ?? urlParamEventTypeId)
      });
    }
  };

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading && !hasPreloaded) return null;
  if (errorCode || !templates.length) return null;

  const eventTypeId = configuredEventTypeId ?? urlParamEventTypeId;
  const parsedEventTypeId = Number(eventTypeId);

  if (!isMeetingBasedEventTypeId(parsedEventTypeId)) return null;

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        isNested={isCondensedView}
        title={
          <Title isNested={isCondensedView}>
            <Translation {...translationMap[parsedEventTypeId]} />
          </Title>
        }
      >
        <Tabs
          className={styles.tabsWrapper}
          value={selectedTab}
          onValueChange={(value) => handleTabChange(value)}
        >
          <TabsList>
            <TabsTrigger
              value="ukIe"
              disabled={!ukIeTemplates.length}
              onClick={() => handleTabClick(ukIeRacesTrackingLabel)}
            >
              <Translation {...TXTWCL_UK_X_Ireland} />
            </TabsTrigger>
            <TabsTrigger
              value="all"
              disabled={!templates.length}
              onClick={() => handleTabClick(allRacesTrackingLabel)}
            >
              <Translation {...TXTCLI_All} />
            </TabsTrigger>
          </TabsList>
          <TabsContent value="ukIe">
            <ScrollableList>
              {ukIeTemplates.length ? renderList(ukIeTemplates) : null}
            </ScrollableList>
          </TabsContent>
          <TabsContent value="all">
            <ScrollableList>{templates.length ? renderList(templates) : null}</ScrollableList>
          </TabsContent>
        </Tabs>

        {selectedTemplate ? (
          <div className={styles.cardWrapper}>
            <RaceInfo marketId={selectedTemplate.marketId} />
            <MarketTemplate
              templateId={selectedTemplate.templateId}
              marketId={selectedTemplate.marketId}
              templateDisplayConfig={
                isCondensedView
                  ? { RACE_CARD: { maxNumberOfRunners: widgetConfig.maxRunners as number } }
                  : undefined
              }
              templateConfig={selectedTemplate.templateConfig}
            />
            <ViewFullRaceCard marketId={selectedTemplate.marketId} />
          </div>
        ) : null}
      </ExpandablePanel>
    </div>
  );
};

SportsNextRacesWidget.model = sportsNextRacesWidgetModel;

export default withWidgetTestId(SportsNextRacesWidget, 'sports-next-races');
